import { HttpClient, HttpEvent, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { UploadOpts } from 'libs/core/src/lib/types/general';
import { Observable, race, throwError, timer } from 'rxjs';
import { filter, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { EnumRequestStatusStatus } from '../models/supply-visibility.model';

@Injectable({ providedIn: 'root' })
export class FileUploadService {
  private readonly http = inject(HttpClient);

  private readonly defaultDelay = 3_000;
  private readonly defaultTimeout = 5 * 60_000;

  uploadFormData<T>(
    url: string,
    fileName: string,
    file: File,
    opts: UploadOpts & { fields?: Record<string, string> } = {},
  ): Observable<HttpEvent<T>> {
    const formData = new FormData();
    formData.append('fileName', fileName);
    formData.append('file', file);

    for (const [k, v] of Object.entries(opts.fields ?? {})) {
      formData.append(k, v);
    }

    const req = new HttpRequest<FormData>('POST', url, formData, {
      params: opts.params,
      reportProgress: opts.reportProgress ?? true,
      responseType: 'json',
    });

    return this.http.request<T>(req);
  }

  uploadWithPollingFns<S extends { status: EnumRequestStatusStatus }>(
    file: File,
    start: (file: File) => Observable<{ requestHash: string }>,
    getStatus: (hash: string) => Observable<S>,
  ): Observable<S> {
    return start(file).pipe(
      switchMap(({ requestHash }): Observable<S> => this.pollRequestStatus(getStatus, requestHash)),
    );
  }

  /**
   * Polls via the provided getStatus() callback until Success,
   * throws on Error/NotFound/Timeout, emits the final status and completes.
   */
  private pollRequestStatus<S extends { status: EnumRequestStatusStatus }>(
    getStatus: (requestHash: string) => Observable<S>,
    requestHash: string,
  ): Observable<S> {
    const success$ = timer(0, this.defaultDelay).pipe(
      switchMap((): Observable<S> => getStatus(requestHash)),
      tap(({ status }): void => {
        if (
          status === EnumRequestStatusStatus.Error ||
          status === EnumRequestStatusStatus.Notfound
        ) {
          throw new Error('Upload failed or not found.');
        }
      }),
      filter(({ status }): boolean => status === EnumRequestStatusStatus.Success),
      take(1),
    );

    const timeout$ = timer(this.defaultTimeout).pipe(
      mergeMap((): Observable<S> => throwError(new Error('Polling timed out.'))),
    );

    return race(success$, timeout$);
  }
}
